import { useEffect, useState, useMemo } from "react";
import { api } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DateInput from "@/components/ui/date-input";
import { formatWaktu } from "@/lib/dates";
import { Search, Loader2, ArrowRight, History, Wallet, Building2, Package, Users, Clock, CalendarDays, X } from "lucide-react";

export const ACTION_META = {
  create: { label: "Ditambahkan", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  update: { label: "Diperbarui", tone: "bg-blue-50 text-blue-700 border-blue-200" },
  delete: { label: "Dihapus", tone: "bg-rose-50 text-rose-700 border-rose-200" },
  import: { label: "Impor Excel", tone: "bg-amber-50 text-amber-700 border-amber-200" },
};

export const ChangeList = ({ changes }) => {
  if (!changes?.length) return null;
  const v = (c, a, b) => (c[a] !== undefined ? c[a] : c[b]);
  return (
    <ul className="mt-2 space-y-1">
      {changes.map((c, i) => (
        <li key={`${c.field}-${i}`} className="text-xs text-slate-600 flex flex-wrap items-center gap-1.5">
          <span className="font-semibold text-slate-700">{c.field}:</span>
          <span className="line-through text-slate-400">{v(c, "old", "dari") || "(kosong)"}</span>
          <ArrowRight className="h-3 w-3 text-slate-400 shrink-0" />
          <span className="text-slate-900 font-medium">{v(c, "new", "ke") || "(kosong)"}</span>
        </li>
      ))}
    </ul>
  );
};

const ENTITY_META = {
  pekerja: { label: "Pekerja", tone: "bg-slate-50 text-slate-700 border-slate-200", icon: Users },
  kontrak: { label: "Keuangan", tone: "bg-violet-50 text-violet-700 border-violet-200", icon: Wallet },
  tagihan_vendor: { label: "Tagihan Vendor", tone: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200", icon: Wallet },
  aset: { label: "Area", tone: "bg-teal-50 text-teal-700 border-teal-200", icon: Building2 },
  barang: { label: "Consumable", tone: "bg-amber-50 text-amber-700 border-amber-200", icon: Package },
  asset: { label: "Asset", tone: "bg-indigo-50 text-indigo-700 border-indigo-200", icon: Package },
  spl: { label: "SPL Lembur", tone: "bg-sky-50 text-sky-700 border-sky-200", icon: Clock },
  cuti: { label: "Cuti", tone: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CalendarDays },
  petty_cash: { label: "Petty Cash", tone: "bg-orange-50 text-orange-700 border-orange-200", icon: Wallet },
  petty_gl: { label: "Petty Cash · GL", tone: "bg-orange-50 text-orange-700 border-orange-200", icon: Wallet },
  petty_periode: { label: "Petty Cash · Periode", tone: "bg-orange-50 text-orange-700 border-orange-200", icon: Wallet },
  lokasi: { label: "Lokasi", tone: "bg-blue-50 text-blue-700 border-blue-200", icon: Building2 },
};
const entityOf = (l) => l.entity || "pekerja";

export default function HistoryView() {
  const [logs, setLogs] = useState(null);
  const [search, setSearch] = useState("");
  const [entity, setEntity] = useState("all");
  const [userName, setUserName] = useState("all");
  const [dari, setDari] = useState("");
  const [sampai, setSampai] = useState("");
  const [opts, setOpts] = useState({ entities: [], users: [] });

  useEffect(() => { api.get("/audit-logs/filters").then((r) => setOpts(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setLogs(null);
    const params = { limit: 300 };
    if (entity !== "all") params.entity = entity;
    if (userName !== "all") params.user_name = userName;
    if (dari) params.dari = dari;
    if (sampai) params.sampai = sampai;
    api.get("/audit-logs", { params }).then((res) => setLogs(res.data)).catch(() => setLogs([]));
  }, [entity, userName, dari, sampai]);

  const filtered = useMemo(() => {
    if (!logs) return [];
    const q = search.toLowerCase();
    return logs.filter((l) =>
      [l.nama, l.nomor_pegawai, l.user_email, l.user_name, ...(l.changes || []).map((c) => c.field)].some((f) => (f || "").toLowerCase().includes(q))
    );
  }, [logs, search]);
  const resetFilter = () => { setEntity("all"); setUserName("all"); setDari(""); setSampai(""); setSearch(""); };
  const adaFilter = entity !== "all" || userName !== "all" || dari || sampai || search;

  return (
    <div className="space-y-5" data-testid="history-view">
      <div className="flex flex-wrap items-end gap-2">
        <div className="relative w-full lg:max-w-xs">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari nama, nomor, admin, field..." className="pl-10 h-10"
            data-testid="history-search-input" />
        </div>
        <Select value={entity} onValueChange={setEntity}>
          <SelectTrigger className="h-10 w-44" data-testid="history-filter-entity"><SelectValue placeholder="Modul" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Modul</SelectItem>
            {opts.entities.map((e) => <SelectItem key={e} value={e}>{ENTITY_META[e]?.label || e}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={userName} onValueChange={setUserName}>
          <SelectTrigger className="h-10 w-48" data-testid="history-filter-user"><SelectValue placeholder="Pengguna" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Pengguna</SelectItem>
            {opts.users.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1.5">
          <DateInput value={dari} onChange={setDari} className="h-10 w-36" data-testid="history-filter-dari" />
          <span className="text-xs text-slate-400">s/d</span>
          <DateInput value={sampai} onChange={setSampai} className="h-10 w-36" data-testid="history-filter-sampai" />
        </div>
        {adaFilter && <Button variant="ghost" size="sm" className="h-10 gap-1 text-slate-500" onClick={resetFilter} data-testid="history-filter-reset"><X className="h-3.5 w-3.5" />Reset</Button>}
        {logs && <span className="text-xs text-slate-500 ml-auto" data-testid="history-count">{filtered.length} log</span>}
      </div>

      {logs === null ? (
        <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 border-slate-200 shadow-none rounded-lg text-center text-slate-400" data-testid="history-empty">
          <History className="h-8 w-8 mx-auto mb-3 text-slate-300" />
          Belum ada riwayat perubahan
        </Card>
      ) : (
        <div className="space-y-3" data-testid="history-list">
          {filtered.map((l) => {
            const meta = ACTION_META[l.action] || { label: l.action, tone: "bg-slate-50 text-slate-700 border-slate-200" };
            const em = ENTITY_META[entityOf(l)] || { label: entityOf(l), tone: "bg-slate-50 text-slate-700 border-slate-200", icon: History };
            const EIcon = em.icon;
            return (
              <Card key={l.id} className="p-4 border-slate-200 shadow-none rounded-lg" data-testid={`history-item-${l.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className={`${meta.tone} font-medium`}>{meta.label}</Badge>
                  <Badge variant="outline" className={`${em.tone} font-medium gap-1`} data-testid={`history-entity-${l.id}`}><EIcon className="h-3 w-3" /> {em.label}</Badge>
                  <span className="font-semibold text-slate-900 text-sm">{l.nama}</span>
                  {l.nomor_pegawai && <span className="text-xs text-slate-500">({l.nomor_pegawai})</span>}
                  <span className="text-xs text-slate-400 ml-auto">{formatWaktu(l.timestamp)}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1.5">
                  Oleh <span className="font-medium text-slate-700">{l.user_name}</span> ({l.user_email})
                </p>
                <ChangeList changes={l.changes} />
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
